import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home, Files, Calendar, Trophy, MessageSquare, Server, Shield, LogOut,
  LogIn, User, Users, Menu, BarChart3, Wifi, TrendingUp, Image, Music,
  HelpCircle, Headphones, Mic, Gamepad2, HardDrive, Camera, MapPin,
  ChevronLeft, ChevronRight, Terminal, ShoppingBag, Radio, Activity, BarChart3 as ChartIcon,
  Gift, Palette, Sparkles, Wand2, Stethoscope, Wrench, CalendarClock, MonitorPlay, UserPlus, Award, LayoutDashboard, FlaskConical, Settings, Wallet, BookOpen, Car, Ticket, Bug, ZoomIn, ZoomOut, RotateCcw, ChevronDown, QrCode, EyeOff, Key, RefreshCw } from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '../lib/utils'
import { modulesAPI, authAPI, menuVisibilityAPI } from '../lib/api'
import { useFavicon } from '../hooks/useFavicon'
import KassaEventBadge from './kassa/KassaEventBadge'
import NotificationBell from './NotificationBell'
import MidiContextBadge from './MidiContextBadge'
import { can } from '../lib/permissions'
import { useAuth } from '../hooks/useAuth'
import ForcePasswordChange from './ForcePasswordChange'
import PubliekeModusBanner from './PubliekeModusBanner'
import MaintenanceGate from './MaintenanceGate'

/* Derive 1-2 letter uppercase initials uit een sitenaam.
   "LAN Server" → "LS", "Bear & Bockie" → "BB", "LAN" → "LN", fallback "LN". */
function _brandInitials(name) {
  if (!name || typeof name !== 'string') return 'LN'
  const words = name.replace(/[&/]/g, ' ').split(/\s+/).filter(w => w && /[A-Za-z]/.test(w[0]))
  if (words.length === 0) return name.slice(0, 2).toUpperCase() || 'LN'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
function _readBrandingCache() {
  try { return JSON.parse(sessionStorage.getItem('brandingCache') || 'null') } catch { return null }
}


/* Sidebar-structuur — herzien in F3.
   Doel: minder groepen, minder dubbels, admin-items geconsolideerd tot 2 items
   onderaan. Modules die uit staan of niet toepasbaar zijn worden gefilterd
   door SidebarContent — hier gewoon declareren waar iets thuishoort. */
export const NAV_SECTIONS = [
  {
    items: [
      { path: '/', icon: Home, label: 'Home' },
    ],
  },
  {
    label: 'Nu op de LAN',
    items: [
      { path: '/programma',    icon: CalendarClock, label: 'Programma',    moduleKey: 'programme' },
      { path: '/seats',        icon: MapPin,        label: 'Zitplaatsen',  moduleKey: 'seats' },
      { path: '/events',       icon: Calendar,      label: 'Nieuws',       moduleKey: 'events' },
    ],
  },
  {
    label: 'Toernooien',
    items: [
      { path: '/tournaments',  icon: Trophy,   label: 'Toernooien',        moduleKey: 'tournaments' },
      { path: '/compos',       icon: Award,    label: 'Compo-uitslagen',   moduleKey: 'compos' },
      { path: '/kempenvision', icon: Radio,    label: 'Kempenvision',      moduleKey: 'kempenvision' },
      { path: '/imagecompo',   icon: Sparkles, label: 'Image Compo',       moduleKey: 'imagecompo' },
    ],
  },
  {
    label: 'Spelen',
    items: [
      { path: '/games',        icon: Gamepad2,  label: 'Game Library' },
      { path: '/game-servers', icon: Server,    label: 'Game-servers',     moduleKey: 'game_servers' },
      { path: '/mini-games',   icon: Gamepad2,  label: 'Mini-games',       moduleKey: 'mini_games' },
      // Game-bestanden (/nas-games) bewust uit de sidebar gehaald — de
      // Game Library dekt nu ook download + pre-cache met dezelfde zip-cache
      // op de NAS. Route + pagina + API blijven bestaan (App.jsx),
      // uncomment de regel hieronder om hem terug in het menu te zetten.
      // { path: '/nas-games',    icon: HardDrive, label: 'Game-bestanden',   moduleKey: 'nas_games' },
    ],
  },
  {
    label: 'Bar',
    items: [
      { path: '/bestellen',    icon: ShoppingBag, label: 'Bestellen',      moduleKey: 'kassa' },
      { path: '/kassa/mij',    icon: Wallet,      label: 'Mijn kassa',     moduleKey: 'kassa' },
      { path: '/prijzenkraam', icon: Gift,        label: 'Prijzenkraam',   moduleKey: 'prizes' },
    ],
  },
  {
    label: 'Community',
    items: [
      { path: '/spelers',        icon: User,          label: 'Spelers' },
      { path: '/teams',          icon: Users,         label: 'Teams',           moduleKey: 'teams' },
      { path: '/mijn-verzoeken', icon: UserPlus,      label: 'Mijn verzoeken',  moduleKey: 'teams' },
      { path: '/stats',          icon: TrendingUp,    label: 'Ranking',         moduleKey: 'stats' },
      { path: '/chat',           icon: MessageSquare, label: 'Chat',            moduleKey: 'chat' },
      { path: '/voice-chat',     icon: Mic,           label: 'Voice-chat',      moduleKey: 'voice_chat' },
      { path: '/screenshots',    icon: Image,         label: 'Screenshots',     moduleKey: 'screenshots' },
      { path: '/polls',          icon: BarChart3,     label: 'Polls',           moduleKey: 'polls' },
      { path: '/music',          icon: Music,         label: 'Muziek',          moduleKey: 'music' },
    ],
  },
  {
    label: "Extra's",
    items: [
      { path: '/files', icon: Files, label: 'Bestanden', moduleKey: 'files' },
    ],
  },
  {
    label: 'Helpdesk',
    items: [
      { path: '/helpdesk', icon: HelpCircle, label: 'Helpdesk',  moduleKey: 'helpdesk' },
      { path: '/wiki',     icon: BookOpen,   label: 'Wiki',      moduleKey: 'wiki' },
      { path: '/feedback', icon: Bug,        label: 'Bug / idee' },
    ],
  },
  {
    label: 'Regie',
    items: [
      { path: '/regie',            icon: LayoutDashboard, label: 'Regie',           adminOnly: true },
      { path: '/regie/toernooien', icon: Trophy,          label: 'Toernooi-regie',  adminOnly: true, moduleKey: 'tournaments' },
      { path: '/regie/servers',    icon: Server,          label: 'Server-regie',    adminOnly: true, moduleKey: 'game_servers' },
      { path: '/schermregie',      icon: MonitorPlay,     label: 'Schermregie',     adminOnly: true, moduleKey: 'screens' },
      { path: '/admin/raffle',     icon: Ticket,          label: 'Raffle-tickets',  adminOnly: true, moduleKey: 'raffle' },
    ],
  },
  {
    label: 'Helpdesk-beheer',
    items: [
      { path: '/helpdesk-panel', icon: Headphones, label: 'Support-panel', helpdeskOnly: true, moduleKey: 'helpdesk' },
    ],
  },
  {
    label: 'Beheer',
    collapsible: true,
    items: [
      { path: '/admin/setup',         icon: Wand2,        label: 'Setup-hub',    adminOnly: true },
      { path: '/admin/profile-items', icon: Sparkles,     label: 'Profielitems', adminOnly: true },
      { path: '/admin/badges',        icon: Award,        label: 'Badges',       adminOnly: true },
      { path: '/admin/qr',            icon: QrCode,       label: 'QR-code',      adminOnly: true },
      { path: '/admin/menu',          icon: Menu,         label: 'Menu-zichtbaarheid', adminOnly: true },
      { path: '/admin/maintenance',   icon: Wrench,       label: 'Onderhouds-modus',   adminOnly: true },
      { path: '/admin',               icon: Wrench,       label: 'Instellingen', adminOnly: true },
      // Externe tools — openen in nieuw tabblad.
      // ETI-LAN keys wordt zelf-gehost via /uploads (SMB-editable op
      // TrueNAS → \\TRUENAS\uploads\eti-keys.html) zodat de tabel niet
      // afhankelijk is van Anthropic-hosting en later bewerkbaar blijft.
      { path: 'ext:eti-keys',         icon: Key,          label: 'ETI-LAN keys', adminOnly: true,
        external: true, href: '/uploads/eti-keys.html' },
      { path: 'ext:resilio',          icon: RefreshCw,    label: 'Resilio Sync', adminOnly: true,
        external: true, href: 'http://192.168.129.171:8888/gui/' },
      { path: '/testhub',             icon: FlaskConical, label: 'Testhub',      adminOnly: true },
    ],
  },
]

/* ── Nav-item, kabinet-stijl ────────────────────────────────────────── */
function NavItem({ item, collapsed, hiddenBadge }) {
  const location = useLocation()
  const Icon = item.icon
  // Een pad met querystring (/admin?tab=badges) matcht anders nooit op pathname.
  const [padDeel, queryDeel] = (item.path || '').split('?')
  const isActive = !item.external && location.pathname === padDeel &&
    (!queryDeel || location.search.includes(queryDeel))

  const className = cn(
    'group flex items-center gap-3 text-sm rounded-[4px] transition-colors',
    'border-l-2 border-transparent',
    collapsed ? 'justify-center px-2 py-2' : 'pl-[10px] pr-3 py-2 -ml-[2px]',
    isActive
      ? 'bg-acid/8 text-acid border-l-acid'
      : 'text-muted-foreground hover:text-foreground hover:bg-white/[.03]',
    hiddenBadge && 'opacity-60'
  )

  const inner = (
    <>
      <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-acid' : 'opacity-80 group-hover:opacity-100')} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {hiddenBadge && !collapsed && (
        <EyeOff className="h-3 w-3 shrink-0 ml-auto text-muted-foreground/70"
                title="Verborgen voor deelnemers — enkel admins zien dit item" />
      )}
    </>
  )

  if (item.external) {
    const href = typeof item.href === 'function' ? item.href() : item.href
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
         title={collapsed ? item.label : undefined} className={className}>
        {inner}
      </a>
    )
  }
  return (
    <Link to={item.path} title={collapsed ? item.label : undefined} className={className}>
      {inner}
    </Link>
  )
}

function SidebarContent({ collapsed, setCollapsed, currentUser, enabledModules, hiddenPaths, handleLogout, branding }) {
  const isAdmin = can(currentUser?.role, 'manage_users')
  const filterItem = (item) => {
    if (item.adminOnly && !can(currentUser?.role, 'manage_users')) return false
    if (item.helpdeskOnly && !can(currentUser?.role, 'helpdesk_access')) return false
    if (item.moduleKey && !enabledModules.includes(item.moduleKey)) return false
    // Per-item verbergen (via /admin/menu): deelnemers zien het niet;
    // admins blijven het item zien mét een `hiddenBadge` in NavItem.
    if (hiddenPaths?.includes(item.path) && !isAdmin) return false
    return true
  }

  // Inklap-state per sectie (alleen voor sections met collapsible:true).
  // Bewaard in localStorage zodat de voorkeur na refresh blijft.
  const COLLAPSE_KEY = 'sidebar-section-collapse'
  const [sectionCollapse, setSectionCollapse] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '{}') } catch { return {} }
  })
  const toggleSection = (label) => setSectionCollapse(prev => {
    const nw = { ...prev, [label]: !prev[label] }
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(nw)) } catch {}
    return nw
  })

  return (
    <div className="flex flex-col h-full">
      {/* Brand — kabinet mark */}
      <div className={cn(
        'flex items-center h-16 border-b border-border shrink-0',
        collapsed ? 'justify-center px-0' : 'px-4 gap-3'
      )}>
        <div className="text-acid font-bold text-2xl leading-none tracking-tight">
          {branding?.brand_logo_url
            ? <img src={branding.brand_logo_url} alt="" className="h-8 w-8 object-contain" />
            : (branding?.brand_mark || _brandInitials(branding?.site_name || 'LAN Server'))}
        </div>
        {!collapsed && (
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground whitespace-nowrap">
            {(branding?.site_name || 'LAN Server')}<br/>
            <span className="opacity-70">{branding?.site_subtitle || ''}</span>
          </div>
        )}
      </div>

      {/* Nav sections — extra bottom-padding + scrollbar-gutter zodat het
          duidelijk is dat er verder gescroll kan worden op mobiel. */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 pb-8 space-y-0.5"
           style={{ scrollbarGutter: 'stable' }}>
        {NAV_SECTIONS.map((section, si) => {
          const visible = section.items.filter(filterItem)
          if (visible.length === 0) return null
          const isCollapsibleSection = !!section.collapsible && !collapsed
          const isFolded = isCollapsibleSection && sectionCollapse[section.label]
          return (
            <div key={si} className={si > 0 ? 'pt-3' : ''}>
              {!collapsed && section.label && (
                isCollapsibleSection ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center justify-between px-3 pb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 hover:text-muted-foreground select-none"
                  >
                    <span>{section.label}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', isFolded && '-rotate-90')} />
                  </button>
                ) : (
                  <p className="px-3 pb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60 select-none">
                    {section.label}
                  </p>
                )
              )}
              {collapsed && si > 0 && (
                <div className="mx-2 mb-2 border-t border-border/40" />
              )}
              {!isFolded && visible.map(item => (
                <NavItem key={item.path} item={item} collapsed={collapsed}
                         hiddenBadge={isAdmin && hiddenPaths?.includes(item.path)} />
              ))}
            </div>
          )
        })}
      </nav>

      {/* MIDI-context + Kassa active-event indicators — beide leven onder
          de menuslijst zodat ze niet met de scroll meebewegen. */}
      <div className="px-2 pb-1 shrink-0 space-y-1">
        <MidiContextBadge collapsed={collapsed} sidebar />
        <KassaEventBadge compact={collapsed} />
        {/* Notificatie-bel voor desktop (mobile-header heeft z'n eigen).
            variant="sidebar" laat de dropdown naar rechts uitschuiven zodat
            hij niet buiten viewport valt. */}
        {currentUser && (
          <div className={cn('flex', collapsed ? 'justify-center' : 'justify-start px-2')}>
            <NotificationBell variant="sidebar" />
          </div>
        )}
      </div>

      {/* Bottom: user + logout + collapse */}
      <div className="border-t border-border p-2 shrink-0 space-y-1">
        {currentUser ? (
          <>
            <div className={cn('flex items-center gap-1', collapsed && 'flex-col')}>
              <Link
                to={`/spelers/${currentUser.id}`}
                title={collapsed ? (currentUser.display_name || currentUser.username) : 'Mijn profiel'}
                className={cn(
                  'flex-1 flex items-center gap-2 px-3 py-2 rounded-[4px] hover:bg-white/[.03] transition-colors text-sm min-w-0',
                  collapsed && 'flex-none justify-center px-2 w-full'
                )}
              >
                <div className="h-6 w-6 rounded-[4px] bg-acid/10 border border-acid/20 flex items-center justify-center shrink-0 overflow-hidden">
                  {currentUser.avatar_url
                    ? <img src={currentUser.avatar_url} alt="" className="w-full h-full object-cover" />
                    : <User className="h-3.5 w-3.5 text-acid" />}
                </div>
                {!collapsed && (
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate leading-tight">
                      {currentUser.display_name || currentUser.username}
                    </p>
                    {currentUser.role === 'superadmin' && (
                      <p className="text-[10px] font-mono tracking-wider text-gold leading-tight">Superadmin</p>
                    )}
                    {currentUser.role === 'admin' && (
                      <p className="text-[10px] font-mono tracking-wider text-peach leading-tight">Admin</p>
                    )}
                  </div>
                )}
              </Link>
              <Link
                to="/instellingen"
                title="Instellingen"
                className={cn(
                  'p-2 rounded-[4px] hover:bg-white/[.03] transition-colors text-muted-foreground hover:text-foreground',
                  collapsed && 'w-full flex justify-center'
                )}
              >
                <Settings className="h-4 w-4" />
              </Link>
            </div>
            {!collapsed && (
              <div className="mb-2">
                <ContentZoom compact />
              </div>
            )}
            <button
              onClick={handleLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 rounded-[4px] text-muted-foreground hover:text-signal hover:bg-signal/10 transition-colors text-sm',
                collapsed && 'justify-center px-2'
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && <span>Logout</span>}
            </button>
          </>
        ) : (
          <Link
            to="/login"
            title={collapsed ? 'Login' : undefined}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-[4px] bg-acid text-primary-foreground font-medium text-sm',
              collapsed && 'justify-center px-2'
            )}
          >
            <LogIn className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Login</span>}
          </Link>
        )}

        <button
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Uitklappen' : 'Inklappen'}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-2 rounded-[4px] text-muted-foreground hover:text-foreground hover:bg-white/[.03] transition-colors text-sm',
            collapsed && 'justify-center px-2'
          )}
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4 shrink-0" />
            : <><ChevronLeft className="h-4 w-4 shrink-0" /><span className="text-xs">Inklappen</span></>
          }
        </button>
      </div>
    </div>
  )
}

export default function Layout() {
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true')
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user: currentUser, updateUser } = useAuth()
  const [enabledModules, setEnabledModules] = useState([])
  const [branding, setBranding] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('brandingCache') || 'null') } catch { return null }
  })
  const location = useLocation()

  useFavicon(currentUser?.role)

  useEffect(() => { localStorage.setItem('sidebar-collapsed', String(collapsed)) }, [collapsed])
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  useEffect(() => {
    fetch('/api/settings/branding').then(r => r.json()).then(d => {
      if (d?.branding) { setBranding(d.branding); try { sessionStorage.setItem('brandingCache', JSON.stringify(d.branding)) } catch {} }
    }).catch(() => {})
  }, [])

  // Rol-refresh: als een admin de rol van deze user in de DB wijzigt terwijl
  // die ingelogd is, staat die verandering niet in localStorage. We checken
  // bij mount én elke 60s + bij tab-focus — zo pikt een reeds-open tab de
  // promotie op zonder dat de user hoeft uit-/in te loggen.
  useEffect(() => {
    if (!localStorage.getItem('auth_token')) return
    let alive = true
    const check = () => {
      authAPI.getCurrentUser()
        .then(r => {
          if (!alive) return
          const verse = r.data?.user
          if (verse && currentUser && verse.role !== currentUser.role) {
            updateUser({ ...currentUser, ...verse })
            window.location.reload()
          }
        })
        .catch(() => {})
    }
    check()
    const t = setInterval(check, 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVis)
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) return
    modulesAPI.getEnabled()
      .then(r => setEnabledModules(r.data.map(m => m.module_key)))
      .catch(() => setEnabledModules([
        'files','events','tournaments','polls','stats','screenshots','music',
        'chat','helpdesk','game_servers','network_tools','mini_games',
        'nas_games','event_media','voice_chat','kassa','kempenvision','imagecompo',
      ]))
  }, [currentUser])

  // Per-item zichtbaarheid ophalen — geen auth vereist. Poll elke 60s zodat
  // een admin die een item on/off toggled bij de deelnemers binnen een minuut
  // effect ziet zonder dat ze hoeven te refreshen.
  const [hiddenPaths, setHiddenPaths] = useState([])
  useEffect(() => {
    let alive = true
    const laad = () => menuVisibilityAPI.getHidden()
      .then(r => { if (alive) setHiddenPaths(r.data.hidden || []) })
      .catch(() => {})
    laad()
    const t = setInterval(laad, 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') laad() }
    document.addEventListener('visibilitychange', onVis)
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    updateUser(null)
    navigate('/login')
  }

  const sidebarProps = { collapsed, setCollapsed, currentUser, enabledModules, hiddenPaths, handleLogout, branding }

  // Geïmporteerde deelnemers moeten eerst hun tijdelijke wachtwoord vervangen.
  // Dit blokkeert de hele app: het tijdelijke wachtwoord staat op papier en is
  // dus niet privé zolang het geldig is.
  if (currentUser?.must_change_password) {
    return (
      <ForcePasswordChange
        onDone={() => {
          const bijgewerkt = { ...currentUser, must_change_password: false }
          try { localStorage.setItem('user', JSON.stringify(bijgewerkt)) } catch {}
          window.location.reload()
        }}
      />
    )
  }

  return (
    <>
      <PubliekeModusBanner />
      <div className="min-h-screen bg-background">
      {/* Desktop sidebar — begint onder de publieke-modus banner via CSS-var
          zodat het BuB-logo niet meer wordt overlapt. */}
      <aside className={cn(
        'hidden md:flex flex-col fixed left-0 bg-card border-r border-border z-40 overflow-hidden transition-[width] duration-300',
        collapsed ? 'w-16' : 'w-56'
      )} style={{ top: 'var(--public-banner-h, 0px)', height: 'calc(100vh - var(--public-banner-h, 0px))' }}>
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile overlay sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-60 bg-card border-r border-border flex flex-col h-full z-10 overflow-y-auto">
            <SidebarContent {...sidebarProps} />
          </aside>
        </div>
      )}

      {/* Content area */}
      <div className={cn(
        'flex flex-col min-h-screen transition-[margin] duration-300',
        collapsed ? 'md:ml-16' : 'md:ml-56'
      )}>
        {/* Content-area krijgt een padding-top gelijk aan de banner-hoogte
            zodat de eerste header niet onder de banner verdwijnt. */}
        <div style={{ paddingTop: 'var(--public-banner-h, 0px)' }} className="shrink-0" />
        {/* Mobile top bar — moet ook onder de banner beginnen */}
        <header className="md:hidden sticky z-30 border-b border-border bg-card/95 backdrop-blur flex items-center gap-3 h-14 px-4 shrink-0"
                style={{ top: 'var(--public-banner-h, 0px)' }}>
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-[4px] hover:bg-white/[.05]">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <span className="text-acid font-bold text-lg leading-none">
              {branding?.brand_logo_url
                ? <img src={branding.brand_logo_url} alt="" className="h-6 w-6 object-contain inline-block" />
                : (branding?.brand_mark || _brandInitials(branding?.site_name || 'LAN Server'))}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{branding?.site_name || 'LAN Server'}</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <ContentZoom />
            {currentUser && <MidiContextBadge />}
            {currentUser && <NotificationBell />}
            {currentUser ? (
              <Link to={`/spelers/${currentUser.id}`} className="flex items-center gap-1.5 text-sm text-acid">
                <User className="h-4 w-4" />
                <span className="text-xs truncate max-w-24">{currentUser.display_name || currentUser.username}</span>
              </Link>
            ) : (
              <Link to="/login" className="text-sm text-acid flex items-center gap-1">
                <LogIn className="h-4 w-4" />Login
              </Link>
            )}
          </div>
        </header>

        {/* Main: fluid padding + gecentreerde inner-wrapper. Content nooit links-tegen-sidebar geplakt.
            De ContentZoom in de top-bar past `document.documentElement.style.--content-zoom` aan;
            we lezen die als CSS `zoom` (voor Chrome/Edge/Safari) plus als fallback transform-scale
            als de browser `zoom` niet ondersteunt. Sidebar/header blijven op 100% zodat de navigatie
            leesbaar blijft ongeacht content-zoom. */}
        <main className="flex-1 w-full overflow-x-hidden" style={{ padding: '1.75rem clamp(1.5rem, 4vw, 3rem) 3rem' }}>
          <div className="mx-auto w-full lanserver-zoom-target" style={{ maxWidth: '1180px' }}>
            <MaintenanceGate user={currentUser}>
              <Outlet />
            </MaintenanceGate>
          </div>
        </main>

        <footer className="border-t border-border bg-card/50 shrink-0">
          <div className="mx-auto py-4 text-center font-mono text-[10px] tracking-widest uppercase text-muted-foreground" style={{ maxWidth: '1180px' }}>
            {branding?.footer_text || 'LAN Server · thuisserver op Proxmox · sess. 12'}
          </div>
        </footer>
      </div>
    </div>
    </>
  )
}

/* Content-only zoom in de top-bar. Alleen `<main>`-content wordt geschaald,
   sidebar/header blijven op 100 % zodat de navigatie leesbaar blijft.
   Waarde persisteert in localStorage. */
function ContentZoom({ compact = false }) {
  const STEPS = [70, 80, 90, 100, 110, 125, 150]
  const [zoom, setZoom] = useState(() => {
    try { return Number(localStorage.getItem('content_zoom')) || 100 }
    catch { return 100 }
  })

  useEffect(() => {
    // CSS `zoom` werkt in Chromium/Safari; Firefox valt terug op transform.
    const el = document.querySelector('.lanserver-zoom-target')
    if (!el) return
    const supportsZoom = CSS.supports?.('zoom', '1') ?? true
    if (supportsZoom) {
      el.style.zoom = zoom === 100 ? '' : String(zoom / 100)
      el.style.transform = ''; el.style.transformOrigin = ''
      el.style.width = ''
    } else {
      el.style.zoom = ''
      el.style.transform = zoom === 100 ? '' : `scale(${zoom / 100})`
      el.style.transformOrigin = 'top left'
      el.style.width = zoom === 100 ? '' : `${10000 / zoom}%`
    }
    try { localStorage.setItem('content_zoom', String(zoom)) } catch {}
  }, [zoom])

  const step = (dir) => {
    const idx = STEPS.indexOf(zoom)
    const nextIdx = idx < 0
      ? STEPS.indexOf(100)
      : Math.max(0, Math.min(STEPS.length - 1, idx + dir))
    setZoom(STEPS[nextIdx])
  }
  const reset = () => setZoom(100)

  return (
    <div className={cn(
           'inline-flex items-center gap-0.5 rounded border border-border bg-card/40 px-1 py-0.5',
           compact && 'w-full justify-between')}
         title="Content-zoom (menu blijft op 100 %)">
      <button type="button" onClick={() => step(-1)} disabled={zoom <= STEPS[0]}
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Uitzoomen">
        <ZoomOut className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={reset}
              className="px-1.5 pf-mono text-[10px] uppercase text-muted-foreground hover:text-foreground min-w-[2.5rem]"
              aria-label="Reset zoom" title="Reset naar 100 %">
        {zoom === 100 ? <RotateCcw className="h-3 w-3 inline" /> : `${zoom}%`}
      </button>
      <button type="button" onClick={() => step(1)} disabled={zoom >= STEPS[STEPS.length - 1]}
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Inzoomen">
        <ZoomIn className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
